import { generateUuidV4 } from '../../util/uuid';
import { apollo_client } from '../app/db_util';
import { MetadataError } from '@ep/error-handling';
import { auth } from '../google/firebase';
import {
  GET_READINESS_GAUGE_INSTANCES_QUERY,
  GET_READINESS_GAUGE_RESULTS_QUERY
} from '../graphql/queries/readiness_gauge_instance';
import {
  DELETE_READINESS_GAUGE_INSTANCE,
  SET_READINESS_GAUGE_INSTANCE_MUTATOR
} from '../graphql/mutations/readiness_gauge_instance';
import {
  Country,
  EntityLabel,
  Question,
  ReadinessGaugeInstance,
  ReadinessGaugeResultDetail,
  Response
} from '@esg/esg-global-types';
import { KPIExtended } from './kpi';
import { CompanyExtended } from '../app/company';

export interface ReadinessGaugeParameters {
  [key: string]: CompanyExtended | Country | null;
  company: CompanyExtended | null;
  country: Country | null;
}

export interface ResponseExtended extends Response {
  question: Question;
}

export interface ReadinessGaugeExtended extends ReadinessGaugeInstance {
  responses: Array<ResponseExtended>;
}

export interface ResultDetailExtended extends Omit<ReadinessGaugeResultDetail, 'kpi'> {
  kpi: KPIExtended;
  responses: Array<ResponseExtended>;
}

// Singular and plural label for model entity.
export const readiness_gauge_instance_label: EntityLabel = {
  one: 'Readiness Gauge Instance',
  many: 'Readiness Gauge Instances'
};

/**
 * Query all readiness gauge instance documents for group.
 * @param {string} group_id Group object of current portal company
 * @param {string} id query a specific readiness gauge instance by id.
 * @returns {Promise<Array<ReadinessGaugeInstance>>}
 */
export const getReadinessGaugeInstances = async (
  group_id: string,
  id?: string
): Promise<Array<ReadinessGaugeInstance>> => {
  try {
    const readiness_gauge_instances: Array<ReadinessGaugeInstance> = await apollo_client
      .query({
        variables: {
          group_id: group_id,
          id: id
        },
        query: GET_READINESS_GAUGE_INSTANCES_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.readiness_gauge_instances;
      });
    return readiness_gauge_instances;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: getReadinessGaugeInstances failed on an unknown error.',
      {
        group_id: group_id
      },
      tracking_id
    );
  }
};

/**
 * Query the results for a readiness gauge instance, calculating if need be.
 * @param {string} group_id Group to query Readiness Gauge results for.
 * @param {ReadinessGaugeInstance} readiness_gauge_instance Readiness Gauge Instance to query results for.
 * @param {Array<Response>} responses Responses submitted for Readiness Gauge Instance.
 * @returns {Promise<ReadinessGaugeExtended>}
 */
export const getReadinessGaugeResults = async (
  group_id: string,
  readiness_gauge_instance: ReadinessGaugeInstance,
  responses: Array<Response>
): Promise<ReadinessGaugeExtended> => {
  try {
    const readiness_gauge_with_results: ReadinessGaugeExtended = await apollo_client
      .query({
        variables: {
          group_id: group_id,
          readiness_gauge_instance: readiness_gauge_instance,
          responses: responses
        },
        query: GET_READINESS_GAUGE_RESULTS_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.readiness_gauge_results;
      });
    return readiness_gauge_with_results;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: getReadinessGaugeResults failed on an unknown error.',
      {
        group_id: group_id,
        readiness_gauge_instance: readiness_gauge_instance,
        responses: responses
      },
      tracking_id
    );
  }
};

export const setReadinessGaugeInstance = async (
  group_id: string,
  readiness_gauge_instance: ReadinessGaugeInstance,
  original_readiness_gauge_instance?: ReadinessGaugeInstance
): Promise<ReadinessGaugeInstance> => {
  try {
    const set_readiness_gauge_instance: ReadinessGaugeInstance = await apollo_client
      .mutate({
        mutation: SET_READINESS_GAUGE_INSTANCE_MUTATOR,
        variables: {
          group_id: group_id,
          readiness_gauge_instance: readiness_gauge_instance,
          user_email: auth.currentUser?.email,
          original_readiness_gauge_instance: original_readiness_gauge_instance
        }
      })
      .then((response) => {
        return response.data.setReadinessGaugeInstance;
      });
    return set_readiness_gauge_instance;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/readiness_gauge/readiness_gauge_instance.ts failed on an unknown error while calling setReadinessGaugeInstance.',
      {},
      tracking_id
    );
  }
};

/**
 * Soft delete a Readiness Gauge Instance.
 * @param {string} group_id ID of Group to delete readiness gauge for.
 * @param {string} readiness_gauge_instance_id ID of readiness gauge to delete.
 * @param {string} readiness_gauge_instance_name Name of readiness gauge to delete.
 * @returns {void}
 */
export const deleteReadinessGaugeInstance = async (
  readiness_gauge_instance_id: string,
  readiness_gauge_instance_name: string,
  group_id: string
): Promise<void> => {
  try {
    await apollo_client.mutate({
      mutation: DELETE_READINESS_GAUGE_INSTANCE,
      variables: {
        readiness_gauge_instance_id: readiness_gauge_instance_id,
        readiness_gauge_instance_name: readiness_gauge_instance_name,
        group_id: group_id,
        user_email: auth.currentUser?.email
      }
    });
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/readiness_gauge/readiness_gauge_instance.ts failed on an unknown error while calling deleteReadinessGaugeInstance.',
      {
        group_id: group_id,
        readiness_gauge_instance_id: readiness_gauge_instance_id,
        readiness_gauge_instance_name: readiness_gauge_instance_name
      },
      tracking_id
    );
  }
};
